/**
 * A 5×7 bitmap font, built in code and uploaded once as a single-row atlas.
 *
 * Damage numbers are the only text drawn *inside* the post chain, so they cannot use
 * the DOM layer the HUD uses. A bitmap font keeps them one draw call (§14.2), keeps
 * tabular advance for free (§11.11 asks for tabular figures), and matches the pixel
 * presentation layer of DECISIONS.md AMD-3 without pulling in a font file.
 */

import * as THREE from 'three';

export const GLYPH_W = 5;
export const GLYPH_H = 7;
export const GLYPH_COUNT = 16;

export const GLYPH_DOT = 10;
export const GLYPH_K = 11;
export const GLYPH_PLUS = 12;
export const GLYPH_MINUS = 13;
export const GLYPH_X = 14;
export const GLYPH_BLANK = 15;

/** Row-major, one string per row, MSB left. Index 0..9 are the digits. */
const GLYPHS: readonly string[] = [
  '01110' + '10001' + '10011' + '10101' + '11001' + '10001' + '01110', // 0
  '00100' + '01100' + '00100' + '00100' + '00100' + '00100' + '01110', // 1
  '01110' + '10001' + '00001' + '00010' + '00100' + '01000' + '11111', // 2
  '11111' + '00010' + '00100' + '00010' + '00001' + '10001' + '01110', // 3
  '00010' + '00110' + '01010' + '10010' + '11111' + '00010' + '00010', // 4
  '11111' + '10000' + '11110' + '00001' + '00001' + '10001' + '01110', // 5
  '00110' + '01000' + '10000' + '11110' + '10001' + '10001' + '01110', // 6
  '11111' + '00001' + '00010' + '00100' + '01000' + '01000' + '01000', // 7
  '01110' + '10001' + '10001' + '01110' + '10001' + '10001' + '01110', // 8
  '01110' + '10001' + '10001' + '01111' + '00001' + '00010' + '01100', // 9
  '00000' + '00000' + '00000' + '00000' + '00000' + '01100' + '01100', // .
  '10000' + '10000' + '10010' + '10100' + '11000' + '10100' + '10010', // k
  '00000' + '00100' + '00100' + '11111' + '00100' + '00100' + '00000', // +
  '00000' + '00000' + '00000' + '11111' + '00000' + '00000' + '00000', // -
  '00000' + '00000' + '10001' + '01010' + '00100' + '01010' + '10001', // x
  '00000' + '00000' + '00000' + '00000' + '00000' + '00000' + '00000', // blank
];

/**
 * Single-row atlas, `GLYPH_COUNT * GLYPH_W` × `GLYPH_H`, one byte per texel.
 * Nearest filtered: the glyphs are pixels, and any smoothing here would be undone
 * by the composite's own upscale anyway.
 */
export function createGlyphTexture(): THREE.DataTexture {
  const w = GLYPH_COUNT * GLYPH_W;
  const data = new Uint8Array(w * GLYPH_H);
  for (let g = 0; g < GLYPH_COUNT; g++) {
    const bits = GLYPHS[g];
    for (let row = 0; row < GLYPH_H; row++) {
      for (let col = 0; col < GLYPH_W; col++) {
        const on = bits.charCodeAt(row * GLYPH_W + col) === 49;
        data[row * w + g * GLYPH_W + col] = on ? 255 : 0;
      }
    }
  }
  const tex = new THREE.DataTexture(data, w, GLYPH_H, THREE.RedFormat, THREE.UnsignedByteType);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.unpackAlignment = 1;
  tex.needsUpdate = true;
  tex.name = 'glyph-atlas';
  return tex;
}

/**
 * Writes an integer into `out` as glyph indices, abbreviating past 999 (§11.11:
 * `1.2k`). Returns the glyph count. Never allocates and never produces a string.
 */
export function writeInteger(value: number, out: Int8Array, offset: number): number {
  let v = Math.abs(Math.round(value));
  let n = offset;
  if (v > 999) {
    const thousands = Math.floor(v / 1000);
    if (thousands >= 100) {
      n = writeDigits(thousands, out, n);
      out[n++] = GLYPH_K;
      return n - offset;
    }
    const tenth = Math.floor((v % 1000) / 100);
    n = writeDigits(thousands, out, n);
    out[n++] = GLYPH_DOT;
    out[n++] = tenth;
    out[n++] = GLYPH_K;
    return n - offset;
  }
  if (v === 0) {
    out[n++] = 0;
    return n - offset;
  }
  n = writeDigits(v, out, n);
  return n - offset;
}

function writeDigits(v: number, out: Int8Array, offset: number): number {
  let digits = 0;
  let t = v;
  while (t > 0) {
    digits++;
    t = Math.floor(t / 10);
  }
  if (digits === 0) digits = 1;
  for (let i = digits - 1; i >= 0; i--) {
    out[offset + i] = v % 10;
    v = Math.floor(v / 10);
  }
  return offset + digits;
}
